'use client';

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { createClient } from '../../lib/supabase/client';
import BrailleSpinner from '@/app/components/BrailleSpinner';
import posthog from 'posthog-js';
import { trackSubscriptionModalViewed, type PaywallContext } from '@/lib/analytics/paywall';

interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPlanSelected?: () => void;
  onAuthRequired?: () => void;
  premiumOnly?: boolean;
  message?: string | null;
  /** Modal heading. Override per surface (e.g. "Manage your lists with Bastion Premium"). */
  title?: string;
  /** Feature name to emphasize for the current trigger (e.g. "Portfolio Manager"). */
  highlightFeature?: string | null;
  analyticsContext?: Omit<PaywallContext, 'trigger'>;
}

export default function SubscriptionModal({ isOpen, onClose, onPlanSelected, onAuthRequired, premiumOnly: _premiumOnly = true, message, title = 'Choose a subscription plan', highlightFeature, analyticsContext }: SubscriptionModalProps) {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('yearly');
  const [currentPlan, setCurrentPlan] = useState<any>(null);
  const [isTrialing, setIsTrialing] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [user, setUser] = useState<any>(null);
  const [planIds, setPlanIds] = useState<{ free?: string; pro?: string; premium?: string }>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [openingPortal, setOpeningPortal] = useState(false);

  const handleOpenPaymentPortal = async () => {
    setOpeningPortal(true);
    try {
      const response = await fetch('/api/subscriptions/portal', { method: 'POST' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create portal session');
      if (data.url) window.location.href = data.url;
    } catch (error) {
      console.error('Error opening payment portal:', error);
    } finally {
      setOpeningPortal(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      trackSubscriptionModalViewed({
        surface: analyticsContext?.surface ?? 'subscription_modal',
        ...analyticsContext,
      });
      setErrorMessage(null);
      setOpeningPortal(false);
      const checkUser = async () => {
        try {
          const supabase = createClient();
          const { data: { user } } = await supabase.auth.getUser();
          setUser(user);
          if (user) fetchSubscriptionStatus();
        } catch (error) {
          console.error('Error checking user:', error);
        }
      };
      checkUser();
    }
  }, [isOpen, analyticsContext]);

  const fetchPlans = async (): Promise<{ free?: string; pro?: string; premium?: string }> => {
    try {
      const plansResponse = await fetch('/api/subscriptions/plans');
      if (plansResponse.ok) {
        const plansData = await plansResponse.json();
        if (plansData.plans && plansData.plans.length > 0) {
          const ids = {
            free: plansData.plans.find((p: any) => p.name === 'Free')?.id,
            pro: plansData.plans.find((p: any) => p.name === 'Pro')?.id,
            premium: plansData.plans.find((p: any) => p.name === 'Premium')?.id,
          };
          setPlanIds(ids);
          return ids;
        }
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
    return {};
  };

  const fetchSubscriptionStatus = async () => {
    try {
      // Plans drive the CTA; fetch them in parallel with status but don't block the UI on either.
      const [statusResponse] = await Promise.all([
        fetch('/api/subscriptions/status'),
        fetchPlans(),
      ]);
      if (statusResponse.ok) {
        const data = await statusResponse.json();
        setIsTrialing(data.isTrialing || false);
        setIsActive(data.isActive || false);
        setCurrentPlan(data.plan);
      }
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    }
  };

  const handlePlanSelect = async (billingCycle: 'monthly' | 'yearly') => {
    if (!user) {
      onClose();
      if (onAuthRequired) onAuthRequired();
      return;
    }
    setErrorMessage(null);
    setLoading(true);

    let planIdForCheckout = planIds.premium;
    if (!planIdForCheckout) {
      const ids = await fetchPlans();
      planIdForCheckout = ids.premium;
    }

    if (!planIdForCheckout) {
      setLoading(false);
      setErrorMessage('Unable to load plan information. Please refresh.');
      return;
    }
    posthog.capture('checkout_initiated', {
      billing_cycle: billingCycle,
      plan: 'premium',
      source: analyticsContext?.surface ?? 'subscription_modal',
      trigger: 'modal',
      page: typeof window !== 'undefined' ? window.location.pathname : undefined,
      list_id: analyticsContext?.list_id,
      metric: analyticsContext?.metric,
      portfolio_id: analyticsContext?.portfolio_id,
      ticker: analyticsContext?.ticker,
    });
    setSelectedPlan(billingCycle);
    try {
      const response = await fetch('/api/subscriptions/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ planId: planIdForCheckout, billingCycle, returnUrl: window.location.href }),
      });
      const data = await response.json();
      if (!response.ok) {
        setErrorMessage(data.error || 'Failed to create checkout session');
        setLoading(false);
        return;
      }
      if (data.success && data.redirectUrl) {
        onPlanSelected?.();
        window.location.href = data.redirectUrl;
        return;
      }
      if (data.url) {
        onPlanSelected?.();
        window.location.href = data.url;
      } else {
        setErrorMessage('Failed to create checkout session. Please try again.');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      setErrorMessage('Error creating checkout session. Please try again.');
    } finally {
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  React.useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopImmediatePropagation();
      onClose();
    };
    document.addEventListener('keydown', handleEscape, true);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', handleEscape, true);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const FeatureRow = ({ name, detail, isLast, highlighted }: { name: string; detail: string; isLast?: boolean; highlighted?: boolean }) => (
    <li
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        // Same padding on every row so checkmarks stay aligned;
        // highlight only changes fill / border weight.
        padding: '10px 12px',
        marginBottom: isLast ? 0 : 6,
        borderRadius: 10,
        background: highlighted ? 'rgba(220,226,236,0.08)' : 'transparent',
        border: highlighted ? '1px solid rgba(220,226,236,0.28)' : '1px solid transparent',
      }}
    >
      <span style={{
        color: highlighted ? '#DCE2EC' : 'rgba(220,226,236,0.55)',
        fontSize: 13,
        lineHeight: '20px',
        flexShrink: 0,
        fontWeight: highlighted ? 700 : 400,
      }}>
        ✓
      </span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
        <span style={{
          fontSize: 15,
          fontWeight: 700,
          letterSpacing: '-0.01em',
          lineHeight: 1.25,
          color: highlighted ? '#DCE2EC' : '#f4f4f5',
        }}>
          {name}
        </span>
        <span style={{
          fontSize: 12,
          fontWeight: 400,
          lineHeight: 1.4,
          color: highlighted ? 'rgba(220,226,236,0.72)' : '#8b919a',
        }}>
          {detail}
        </span>
      </div>
    </li>
  );

  /* Premium-only popup: Free showcase is already on the page; comparing
     against Free here just dilutes the offer. */
  const premiumFeatures = [
    { name: 'Stock Verdict', detail: 'Valuation and investment strategy for 4,000+ stocks. Days of hedge fund analyst research, done in 2 minutes.' },
    { name: 'Radar', detail: 'Find your next investment idea: top-ranked stocks across all 9 Bastion strategies.' },
    { name: 'Portfolio Manager', detail: 'Check if Bastion algorithms added the stock you care about to their portfolios.' },
  ];

  const isCurrentPremium = (isActive || isTrialing) && (currentPlan?.name === 'Premium' || currentPlan?.name === 'Pro');

  const monthlyPrice = 29;
  const yearlyPrice = 249;
  const yearlyMonthly = Math.round((yearlyPrice / 12) * 100) / 100;
  const savingsPercent = Math.round(((monthlyPrice * 12 - yearlyPrice) / (monthlyPrice * 12)) * 100);

  const modalContent = (
    <div
      className="fixed inset-0 z-[13000] overflow-y-auto"
      data-bastion-top-overlay=""
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 13000,
        background: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(2px)',
        animation: 'fadeInBackdrop 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
      onClick={onClose}
    >
      <div className="flex min-h-full items-start sm:items-center justify-center p-3 sm:p-4">
      <div
        className="relative w-full"
        style={{ maxWidth: '480px' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 text-zinc-400 hover:text-white transition-colors p-1.5 rounded hover:bg-zinc-800 cursor-pointer z-10"
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Solid container */}
        <div
          className="p-3 sm:p-5"
          style={{
            position: 'relative',
            background: '#1a1a1a',
            borderRadius: '16px',
            border: '1px solid rgba(63, 63, 70, 0.5)',
            boxShadow: '0 0 0 1px rgba(255, 255, 255, 0.05), 0 8px 40px rgba(0, 0, 0, 0.6)',
          }}
        >
          {/* Header */}
          <h2
            className="text-center font-semibold text-white mb-4"
            style={{ fontSize: '1.1rem', letterSpacing: '-0.01em' }}
          >
            {title}
          </h2>

          {/* Custom message */}
          {message && (
            <p className="text-center text-sm mb-4" style={{ color: 'rgba(255, 255, 255, 0.55)', lineHeight: 1.5 }}>
              {message}
            </p>
          )}

          {/* Error message */}
          {errorMessage && (
            <div
              className="text-sm text-center px-3 py-3 rounded-lg mb-3"
              style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#f87171', fontSize: '0.8rem', lineHeight: '1.5' }}
            >
              {errorMessage.includes('Payment failed') ? (
                <>
                  Payment failed. Please{' '}
                  <button onClick={handleOpenPaymentPortal} disabled={openingPortal} className="underline hover:text-white transition-colors font-medium" style={{ color: '#DCE2EC', cursor: openingPortal ? 'wait' : 'pointer' }}>
                    {openingPortal ? 'Opening...' : 'update your payment method'}
                  </button>{' '}and try again.
                </>
              ) : errorMessage}
              <button onClick={() => setErrorMessage(null)} className="block mx-auto mt-2 text-xs text-zinc-400 hover:text-white transition-colors underline">Dismiss</button>
            </div>
          )}

          {/* Billing toggle (matches /subscription) */}
          {!isCurrentPremium && (
            <div className="mb-5 flex justify-center">
              <div className="inline-flex items-center p-1 rounded-full" style={{ background: 'rgba(20, 20, 20, 0.95)', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
                <button
                  onClick={() => setBillingCycle('monthly')}
                  className="px-5 py-2 rounded-full text-sm font-medium transition-all duration-200"
                  style={{
                    background: billingCycle === 'monthly' ? 'rgba(220, 226, 236, 0.15)' : 'transparent',
                    color: billingCycle === 'monthly' ? '#DCE2EC' : '#a1a1aa',
                    border: billingCycle === 'monthly' ? '1px solid rgba(220, 226, 236, 0.35)' : '1px solid transparent',
                    cursor: 'pointer',
                  }}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingCycle('yearly')}
                  className="px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 inline-flex items-center gap-2"
                  style={{
                    background: billingCycle === 'yearly' ? 'rgba(220, 226, 236, 0.15)' : 'transparent',
                    color: billingCycle === 'yearly' ? '#DCE2EC' : '#a1a1aa',
                    border: billingCycle === 'yearly' ? '1px solid rgba(220, 226, 236, 0.35)' : '1px solid transparent',
                    cursor: 'pointer',
                  }}
                >
                  Annual
                  <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded" style={{ background: billingCycle === 'yearly' ? 'rgba(220, 226, 236, 0.25)' : 'rgba(220, 226, 236, 0.1)', color: '#DCE2EC', letterSpacing: '0.04em' }}>
                    Save {savingsPercent}%
                  </span>
                </button>
              </div>
            </div>
          )}

          {/* Premium-only card */}
          <div className="relative mx-auto" style={{ isolation: 'isolate', maxWidth: 420 }}>
            <div className="absolute pointer-events-none" style={{ inset: '-30px', background: 'radial-gradient(ellipse at center, rgba(220, 226, 236, 0.18) 0%, rgba(220, 226, 236, 0.06) 40%, transparent 70%)', filter: 'blur(20px)', zIndex: -1 }} />
            <div
              className="flex flex-col rounded-xl transition-all duration-200 relative"
              style={{ background: 'rgba(20, 22, 28, 0.95)', border: '1px solid rgba(220, 226, 236, 0.45)', borderRadius: '12px' }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(220, 226, 236, 0.65)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(220, 226, 236, 0.45)'; }}
            >
              <div className="p-5 flex flex-col flex-1">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-white" style={{ fontSize: '1.5rem' }}>Premium</h3>
                    {!isCurrentPremium && billingCycle === 'yearly' && (
                      <span className="text-xs font-semibold px-2 py-0.5 rounded" style={{ background: 'rgba(220, 226, 236, 0.15)', color: '#DCE2EC' }}>Save {savingsPercent}%</span>
                    )}
                  </div>
                  {billingCycle === 'monthly' ? (
                    <>
                      <div className="mt-1 mb-0.5 flex items-baseline">
                        <span className="font-bold text-white" style={{ fontSize: '2rem', letterSpacing: '-0.02em' }}>${monthlyPrice}</span>
                        <span className="text-sm ml-1" style={{ color: '#71717a' }}>/month</span>
                      </div>
                      <p className="text-sm leading-snug" style={{ color: '#71717a' }}>Full access, billed monthly</p>
                    </>
                  ) : (
                    <>
                      <div className="mt-1 mb-0.5 flex items-baseline">
                        <span className="font-bold text-white" style={{ fontSize: '2rem', letterSpacing: '-0.02em' }}>${yearlyMonthly}</span>
                        <span className="text-sm ml-1" style={{ color: '#71717a' }}>/month</span>
                      </div>
                      <p className="text-sm leading-snug" style={{ color: '#71717a' }}>
                        Billed annually at <span style={{ color: '#d4d4d8', fontWeight: 500 }}>${yearlyPrice}/year</span>
                        {' '}<span className="line-through ml-1" style={{ color: '#52525b' }}>${monthlyPrice * 12}</span>
                      </p>
                    </>
                  )}
                </div>

                <ul className="flex flex-col gap-2 mt-6">
                  {premiumFeatures.map((f, i) => (
                    <FeatureRow
                      key={i}
                      name={f.name}
                      detail={f.detail}
                      isLast={i === premiumFeatures.length - 1}
                      highlighted={Boolean(highlightFeature && f.name === highlightFeature)}
                    />
                  ))}
                </ul>

                <div className="mt-6">
                  {isCurrentPremium ? (
                    <button disabled className="w-full py-2.5 rounded-lg text-sm font-semibold cursor-not-allowed" style={{ background: 'rgba(220, 226, 236, 0.08)', color: 'rgba(220, 226, 236, 0.5)', border: '1px solid rgba(220, 226, 236, 0.15)' }}>
                      Current Plan
                    </button>
                  ) : (
                    <button
                      onClick={() => handlePlanSelect(billingCycle)}
                      disabled={loading}
                      className={`w-full py-2.5 rounded-lg text-sm font-semibold transition-colors duration-200 cursor-pointer ${loading ? 'opacity-50 cursor-wait' : ''}`}
                      style={{ background: '#DCE2EC', color: '#1a1a1a', border: 'none' }}
                      onMouseEnter={(e) => { if (!loading) e.currentTarget.style.filter = 'brightness(1.1)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.filter = 'brightness(1)'; }}
                    >
                      {loading ? (
                        <div className="flex items-center justify-center gap-1.5">
                          <BrailleSpinner label="" size={14} color="#000000" />
                          Processing...
                        </div>
                      ) : billingCycle === 'yearly' ? 'Get Premium Annual' : 'Get Premium'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Legal text */}
          <div className="mt-4">
            <p className="text-xs leading-relaxed text-center" style={{ color: 'rgba(255,255,255,0.35)' }}>
              By starting your subscription, you agree to our{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: 'rgba(255,255,255,0.5)' }}>Terms</a>,{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: 'rgba(255,255,255,0.5)' }}>Privacy Policy</a>, and{' '}
              <a href="/refund" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: 'rgba(255,255,255,0.5)' }}>Refund Policy</a>.
              {' '}This is a recurring subscription.
              {' '}Investment Bastion does not provide investment advice, and investing involves risk.
            </p>
          </div>
        </div>
      </div>
      </div>

      <style jsx>{`
        @keyframes fadeInBackdrop {
          from { opacity: 0; backdrop-filter: blur(0px); }
          to { opacity: 1; backdrop-filter: blur(2px); }
        }
      `}</style>
    </div>
  );

  if (typeof window !== 'undefined') {
    return createPortal(modalContent, document.body);
  }
  return null;
}
